// Real dashboard backed by /api/dashboard/overview + /api/dashboard/activity.
// Visual hews to the prototype: 4+4 KPI grid, Agent performance + Integration
// health side by side, Recent activity feed below.

import { useQuery } from '@tanstack/react-query';
import { Clock, Plus, ArrowRight, FileText, Inbox, Zap, User, AlertTriangle } from 'lucide-react';
import type { ActivityIcon } from '@qualibot/shared';
import { fetchDashboardOverview, fetchRecentActivity } from '@/lib/dashboard-api';
import { Card } from '@/components/primitives/Card';
import { KpiCard } from '@/components/primitives/KpiCard';
import { BarRow } from '@/components/primitives/BarRow';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { PageContainer, PageHeader } from '@/components/PageHeader';

const ACTIVITY_ICON = {
  doc: FileText,
  inbox: Inbox,
  zap: Zap,
  user: User,
  warning: AlertTriangle,
} as const satisfies Record<ActivityIcon, typeof FileText>;

function formatCurrency(n: number): string {
  return `$${n.toFixed(2)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

export function DashboardPage() {
  const overview = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: fetchDashboardOverview,
  });
  const activity = useQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: fetchRecentActivity,
  });

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        description="Health and performance across all agents in Enterprise Demo"
        actions={
          <>
            <Btn icon={Clock}>Last 30 days</Btn>
            <Btn kind="primary" icon={Plus}>
              Create agent
            </Btn>
          </>
        }
      />

      {overview.isError && (
        <Card>
          <Empty
            title="Couldn't load dashboard"
            body="The dashboard service is unreachable. Check the API and try again."
            actions={<Btn onClick={() => overview.refetch()}>Retry</Btn>}
          />
        </Card>
      )}

      {overview.data && (
        <>
          <KpiGrid>
            <KpiCard
              label="Active agents"
              value={overview.data.summary.activeAgents}
              delta="seed data"
            />
            <KpiCard
              label="Conversations"
              value={formatNumber(overview.data.summary.conversations)}
              delta="last 24 hours"
            />
            <KpiCard
              label="Tool calls"
              value={formatNumber(overview.data.summary.toolCalls)}
              delta="across MCP gateway"
            />
            <KpiCard
              label="Human handoffs"
              value={overview.data.summary.humanHandoffs}
              delta="needs human"
            />
          </KpiGrid>

          <div className="mt-3.5">
            <KpiGrid>
              <KpiCard
                label="Failed tool calls"
                value={overview.data.summary.failedTools}
                delta={
                  overview.data.summary.toolCalls
                    ? `${((overview.data.summary.failedTools / overview.data.summary.toolCalls) * 100).toFixed(2)}% of calls`
                    : '0% of calls'
                }
              />
              <KpiCard
                label="Avg response time"
                value={overview.data.summary.averageResponseTime}
                delta="tool latency p50"
              />
              <KpiCard
                label="Workflow runs"
                value={formatNumber(overview.data.summary.workflowRuns)}
                delta="all workflows"
              />
              <KpiCard
                label="LLM cost"
                value={formatCurrency(overview.data.summary.llmCost)}
                delta="month to date"
              />
            </KpiGrid>
          </div>

          <div
            className="grid gap-3.5 mt-3.5"
            style={{ gridTemplateColumns: '1.4fr 1fr' }}
          >
            <Card>
              <div className="flex items-center justify-between mb-4">
                <span className="font-semibold text-[14px]">Agent performance</span>
                <span className="text-[12px] text-text-3">share of conversations</span>
              </div>
              <div className="flex flex-col gap-3">
                {overview.data.agentShare.map((a) => (
                  <BarRow
                    key={a.agentId}
                    label={a.name.replace('Enterprise ', '')}
                    pct={a.share}
                  />
                ))}
              </div>
            </Card>

            <Card>
              <div className="font-semibold text-[14px] mb-3.5">Integration health</div>
              <div className="flex flex-col">
                {overview.data.integrationHealth.map((h, i) => (
                  <div
                    key={h.name}
                    className="flex items-center justify-between py-2.5"
                    style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}
                  >
                    <span className="text-[13px]">{h.name}</span>
                    <StatusBadge status={h.status} />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      <Card className="mt-3.5">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-[14px]">Recent activity</span>
          <Btn kind="ghost" icon={ArrowRight}>
            Open inbox
          </Btn>
        </div>
        {activity.data && activity.data.length > 0 ? (
          <div className="flex flex-col">
            {activity.data.map((a, i) => {
              const Icon = ACTIVITY_ICON[a.icon] ?? Inbox;
              const isWarn = a.tone === 'warn';
              const isAccent = a.tone === 'accent';
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-3 py-2.5"
                  style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}
                >
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: isWarn
                        ? 'var(--warn-soft)'
                        : isAccent
                          ? 'var(--accent-soft)'
                          : 'var(--surface-2)',
                      color: isWarn
                        ? 'var(--warn)'
                        : isAccent
                          ? 'var(--accent-strong)'
                          : 'var(--text-2)',
                      border:
                        isWarn || isAccent ? 'none' : '1px solid var(--border)',
                    }}
                  >
                    <Icon size={14} strokeWidth={1.7} />
                  </span>
                  <span className="flex-1 text-[13.5px]">{a.text}</span>
                  <span className="text-[12px] text-text-3 whitespace-nowrap">{a.time}</span>
                </div>
              );
            })}
          </div>
        ) : activity.isLoading ? (
          <div className="text-[13px] text-text-3 py-4 text-center">Loading activity…</div>
        ) : (
          <Empty title="No activity yet" body="Conversations and tool calls will land here." />
        )}
      </Card>
    </PageContainer>
  );
}

function KpiGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="grid gap-3.5"
      style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
    >
      {children}
    </div>
  );
}
